import { SLOT_NAMES } from "./constants";

const SLOT_VALUES = Object.values(SLOT_NAMES).sort((a, b) => b.length - a.length);

export const UNSLOTTED = "Unslotted";

export function inferSlotName(component) {
    if (component?.slotName) return component.slotName;
    const hay = `${component?.name || ""} ${component?.description || ""}`.toLowerCase();
    const hit = SLOT_VALUES.find((slot) => hay.includes(slot.toLowerCase()));
    return hit || UNSLOTTED;
}

export function slotFamily(slot) {
    const name = slot || UNSLOTTED;
    if (name === UNSLOTTED) return "other";
    if (/^UM Rules|^Rules -/i.test(name) || /Precert|Referral|Non Elected PCP/i.test(name)) return "um";
    if (/Vendor |Upswing/i.test(name)) return "vendor";
    if (
        /Exclusion|Limit|OOP Max|Deductible|COB |Repricer|Invalid Member|Benefit Determination|Default Component|Member UDT|Bank Account|Non Par|Vendor Invoice|Network Pricing/i.test(name)
    ) {
        return "rules";
    }
    return "benefit";
}

const FAMILY_ORDER = { benefit: 0, rules: 1, um: 2, vendor: 3, other: 4 };
const FAMILY_LABEL = {
    benefit: "Benefits",
    rules: "Plan rules",
    um: "Utilization management",
    vendor: "Vendors",
    other: "Other",
};

export function groupComponentsBySlot(components, parseCats) {
    const bySlot = new Map();
    for (const component of components || []) {
        const slot = inferSlotName(component);
        const weight = Number.isFinite(Number(component.weight)) ? Number(component.weight) : null;
        const categories = new Set();
        for (const provision of component.provisions || []) {
            for (const name of parseCats([provision]) || []) categories.add(name);
        }
        const current = bySlot.get(slot) || {
            slot,
            family: slotFamily(slot),
            weight,
            components: [],
            categories: new Set(),
            provisionCount: 0,
        };
        current.components.push(component);
        if (weight != null && (current.weight == null || weight > current.weight)) current.weight = weight;
        for (const name of categories) current.categories.add(name);
        current.provisionCount += component.provisions?.length || component.provisionCount || 0;
        bySlot.set(slot, current);
    }

    return [...bySlot.values()]
        .map((group) => ({
            ...group,
            categories: [...group.categories].sort((a, b) => a.localeCompare(b)),
            components: group.components.slice().sort((a, b) => (Number(b.weight) || 0) - (Number(a.weight) || 0)),
        }))
        .sort((a, b) => {
            const fam = FAMILY_ORDER[a.family] - FAMILY_ORDER[b.family];
            if (fam) return fam;
            const w = (Number(b.weight) || 0) - (Number(a.weight) || 0);
            if (w) return w;
            return a.slot.localeCompare(b.slot);
        });
}

export function familyLabel(family) {
    return FAMILY_LABEL[family] || family;
}

const POS_HINTS = [
    [/telemed|telehealth|virtual visit/i, "02"],
    [/urgent care/i, "20"],
    [/emergency room|\ber\b/i, "23"],
    [/walk-?in|retail health/i, "17"],
    [/office visit|physician services/i, "11"],
    [/clinic visit/i, "22"],
    [/home health/i, "12"],
];

/**
 * CPT/HCPCS checks need a claim type and usually a place of service.
 * Without those, HRP exact-matches only the plan's "all other services"
 * catch-all — which is how Check on 99202 looked empty.
 */
export function askDefaultsForBrowse({ slot, category, codeType } = {}) {
    const hay = `${category || ""} ${slot || ""}`;
    const institutional = /revenue|ub-?04|institutional/i.test(codeType || "");
    let placeOfService = null;
    for (const [re, pos] of POS_HINTS) {
        if (re.test(hay)) {
            placeOfService = pos;
            break;
        }
    }
    return {
        claimType: institutional ? "Institutional" : "Professional",
        placeOfService,
    };
}

/** Prefer the category that belongs to this slot over the alphabetical first chip. */
export function categoryForSlotCodes(group, focusCategory) {
    if (focusCategory) return focusCategory;
    const cats = group?.categories || [];
    if (!cats.length) return null;
    const slot = (group.slot || "").toLowerCase();
    let best = { name: cats[0], score: 0 };
    for (const name of cats) {
        const n = name.toLowerCase();
        let score = 0;
        if (n === slot) score = 3;
        else if (slot && (n.includes(slot) || slot.includes(n))) score = 2;
        else if (/telemed|telehealth/.test(slot) && /telemed|telehealth|virtual visit/.test(n)) score = 2;
        if (score > best.score) best = { name, score };
    }
    return best.name;
}

export function slotsMentioningCategory(slotGroups, category) {
    const needle = (category || "").trim().toLowerCase();
    if (!needle) return [];
    return slotGroups.filter((group) =>
        group.categories.some((name) => name.toLowerCase() === needle || name.toLowerCase().includes(needle))
    );
}
